<template>
  <section class="uiv" v-if="open">
    <modal
      v-model="open"
      :backdrop="false"
      :footer="false"
      :dismiss-btn="false"
      :keyboard="false"
      :auto-focus="true"
    >
      <div slot="title" class="form-flex">
        <h3 class="modal-title">Sign In to Your Account</h3>
        <a
          role="button"
          class="close"
          aria-label="close"
          @click="loginDismissed">&times;</a>
      </div>
      <template slot="default">
        <alert type="info">
          <p>For your data protection, please log in to access this feature.</p>
        </alert>
        <form
          class="login-form"
          @submit.prevent="attemptLogin"
          v-if="!enforceSSO">
          <input
            type="hidden"
            name="authenticity_token"
            :value="gblcs">
          <div class="form-group form-group-lg">
            <label for="current-password">Password</label>
            <input
              :type="showPassword ? 'text' : 'password'"
              id="current-password"
              autocomplete="current-password"
              v-model="password"
              class="form-control fs-block"
              data-action="auto-focus"
            >
          </div>

          <div class="form-group">
            <label for="passwordCheckbox">
              <input
                type="checkbox"
                v-model="showPassword"
                id="passwordCheckbox" > Show Password
            </label>
          </div>

          <div class="form-flex">
            <div class="form-group form-group-lg">
              <button
                type="submit"
                class="btn btn-block btn-success btn-lg"
                :disabled="!password || requestSent"
              >
                Sign In
              </button>
            </div>
            <div class="form-group form-group-lg">
              <a
                :href="forgotPasswordUrl"
                alt=""
                target="_blank"> Forgot your password? </a>
            </div>
          </div>

          <alert
            type="danger"
            v-for="(error, index) in loginErrors"
            :key="index"
            :duration="4000"
            @dismissed="loginErrors.splice(index, 1)"
          >
            {{ error }} You may need to try again later or <a :href="signOutUrl">sign out</a> and
            try again.
          </alert>
        </form>

        <div v-if="!enforceSSO">
          <p class="divider"><span>or</span></p>
        </div>

        <OneTimeCode
          ref="otc"
          :email="email"
          :gblcs="gblcs"
          :disabled="requestSent"
          @login-success="onOtcLoginSuccess"
          @otc-requested="otcCodeRequested = $event"
        />

        <template v-if="!otcCodeRequested">
          <template v-for="id in nonGoogleSsoProviders">
            <form
              v-if="ssoProviderInfo[id]"
              class="form-flex-center form-group"
              method="POST"
              :action="ssoProviderInfo[id].url"
              :key="id"
            >
              <input
                type="hidden"
                name="authenticity_token"
                :value="gblcs" >
              <button type="submit" class="btn btn-lg btn-default">
                Sign in through {{ ssoProviderInfo[id].name }}
              </button>
            </form>
          </template>

          <form
            v-if="ssoProviderInfo.google_oauth2 && ssoProviderInfo.google_oauth2.url"
            method="POST"
            :action="ssoProviderInfo.google_oauth2.url"
            class="form-flex-center"
          >
            <input
              type="hidden"
              name="authenticity_token"
              :value="gblcs" >
            <div class="form-group form-group-lg">
              <button type="submit" class="btn btn-block btn-lg btn-primary form-control">
                <img
                  src="~@/assets/google-auth.png"
                  alt=""
                  class="btn-google">
                Sign in with Google
              </button>
            </div>
          </form>
        </template>
      </template>
    </modal>

  </section>
</template>

<script>
import { postUserLogin } from '@/api/accounts'
import { goalbookUrls } from '@/links'
import { getCookie, setCrossSiteLogin } from '@/api/request_handler'
import OneTimeCode from '@/components/OneTimeCode.vue'

export default {
  name: 'LoginModal',
  components: { OneTimeCode },
  props: {
    email: {
      type: String,
      required: true,
    },
    ssoProviders: {
      type: Array,
      default: () => [],
    },
    ssoProviderInfo: {
      type: Object,
      default: () => {},
    },
    enforceSSO: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      open: true,
      otcCodeRequested: false,
      password: '',
      showPassword: false,
      requestSent: false,
      loginErrors: [],
      forgotPasswordUrl: goalbookUrls.forgotPassword,
      signOutUrl: goalbookUrls.signOut,
      gblcs: decodeURIComponent(getCookie('gblcs')),
    }
  },
  computed: {
    nonGoogleSsoProviders() {
      return this.ssoProviders.filter((id) => id !== 'google_oauth2')
    },
  },
  methods: {
    async attemptLogin() {
      this.requestSent = true
      try {
        await postUserLogin(this.email, this.password, this.gblcs)
        this.$emit('login-success')
        this.closeModal()
      } catch (err) {
        this.loginErrors.push(err.message || 'There was a problem with your login request.')
      } finally {
        this.requestSent = false
      }
    },
    onOtcLoginSuccess() {
      this.$emit('login-success')
      this.closeModal()
    },
    closeModal() {
      if (this.$refs.otc && this.$refs.otc.reset) {
        this.$refs.otc.reset()
      }
      this.open = false
    },
    loginDismissed() {
      this.$emit('login-dismissed')
      this.$sendEvent('Login Modal Dismissed')
      this.closeModal()
    },
  },
  async created() {
    this.$sendEvent('Login Modal Shown')
    // need a way to set the cookie before it exists
    await setCrossSiteLogin()
    this.gblcs = decodeURIComponent(getCookie('gblcs'))
    // if the cookie doesn't exist, they won't be able to log in until the get one from the sign in via users app
    if (!this.gblcs) {
      window.location.href =
        this.signOutUrl + `?return_url=${encodeURIComponent(window.location.href)}`
    }
  },
}
</script>

<style scoped lang="scss">
.divider {
  border-bottom: 1px dotted #ccc;
  margin-bottom: 30px;
  margin-left: 30px;
  margin-right: 30px;
  height: 20px;

  span {
    position: relative;
    text-align: center;
    height: 40px;
    width: 40px;
    display: block;
    margin: 0 auto;
    top: 5px;
    background: white;
    font-size: 18px;
    font-style: italic;
    color: #6e6e6e;
  }
}
.login-form {
  font-size: 18px;
}
a.close {
  font-size: 1.25em;
}
.btn-google {
  margin: 0 1em 0 -1em;
  background-repeat: no-repeat;
  background-color: #e6e6e6;
  float: left;
  position: relative;
  top: -11px;
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  height: inherit;
  border: 1px solid #ccc;
}

.form-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  div {
    flex-basis: 40%;
  }
}

.form-flex-center {
  display: flex;
  justify-content: center;
  & > * {
    flex-basis: 60%;
  }
}


label {
  font-weight: normal;
}
</style>
